import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getServices } from '@/api/api';
import { Network, Share2 } from 'lucide-react';

const ServiceDependencyGraph = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const servicesData = await getServices();
      setServices(servicesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  // Build dependency map
  const buildDependencyLevels = () => {
    const levels = [];
    const processed = new Set();

    // Find root nodes (nodes with no incoming edges)
    const hasIncoming = new Set();
    services.forEach(service => {
      service.dependencies.forEach(dep => hasIncoming.add(dep));
    });

    // Level 0: Root nodes
    const rootNodes = services.filter(s => !hasIncoming.has(s.name));
    if (rootNodes.length > 0) {
      levels.push(rootNodes);
      rootNodes.forEach(n => processed.add(n.name));
    }

    // Build subsequent levels
    let currentLevel = 0;
    while (processed.size < services.length && currentLevel < 10) {
      const nextLevel = [];
      services.forEach(service => {
        if (!processed.has(service.name)) {
          // Check if all dependencies are in processed
          const allDepsProcessed = service.dependencies.length === 0 || 
            service.dependencies.every(dep => processed.has(dep));
          if (allDepsProcessed) {
            nextLevel.push(service);
            processed.add(service.name);
          }
        }
      });

      if (nextLevel.length > 0) {
        levels.push(nextLevel);
      } else {
        // Add remaining services to avoid infinite loop
        services.forEach(service => {
          if (!processed.has(service.name)) {
            nextLevel.push(service);
            processed.add(service.name);
          }
        });
        if (nextLevel.length > 0) {
          levels.push(nextLevel);
        }
        break;
      }
      currentLevel++;
    }

    return levels;
  };

  const getCloudProviderColor = (provider) => {
    const colors = {
      AWS: '#FF9900',
      GCP: '#4285F4',
      IBM: '#054ADA',
      AZURE: '#0078D4',
    };
    return colors[provider] || '#6B7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Network className="w-12 h-12 animate-spin text-blue-500" />
      </div>
    );
  }

  const levels = buildDependencyLevels();

  return (
    <div className="p-6 space-y-6" data-testid="service-dependency-graph">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Service Dependency Graph</h2>
        <p className="text-gray-600 mt-1">Visualize microservice dependencies across clouds</p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#FF9900' }} />
          <span>AWS</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#4285F4' }} />
          <span>GCP</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#054ADA' }} />
          <span>IBM</span>
        </div>
        <div className="flex items-center gap-2">
          <Share2 className="w-4 h-4 text-gray-400" />
          <span>Dependency Link</span>
        </div>
      </div>

      {/* Graph Container */}
      <Card>
        <CardContent className="p-6">
          <div className="relative" style={{ minHeight: '500px' }}>
            {/* SVG for connections */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
              {levels.map((level, levelIdx) =>
                level.map((service, serviceIdx) =>
                  service.dependencies.map((depName) => {
                    const depService = services.find(s => s.name === depName);
                    if (!depService) return null;

                    // Find positions
                    const depLevel = levels.findIndex(l => l.some(s => s.name === depName));
                    const depIdx = levels[depLevel]?.findIndex(s => s.name === depName);

                    if (depLevel === -1 || depIdx === -1) return null;

                    // Calculate positions (approximate)
                    const x1 = 100 + levelIdx * 250 + 100;
                    const y1 = 100 + serviceIdx * 120 + 40;
                    const x2 = 100 + depLevel * 250 + 100;
                    const y2 = 100 + depIdx * 120 + 40;

                    return (
                      <line
                        key={`${service.name}-${depName}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="#D1D5DB"
                        strokeWidth="2"
                        markerEnd="url(#arrowhead)"
                      />
                    );
                  })
                )
              )}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="10"
                  refX="9"
                  refY="3"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3, 0 6" fill="#D1D5DB" />
                </marker>
              </defs>
            </svg>

            {/* Nodes */}
            <div className="relative" style={{ zIndex: 1 }}>
              {levels.map((level, levelIdx) => (
                <div
                  key={levelIdx}
                  className="absolute"
                  style={{
                    left: `${100 + levelIdx * 250}px`,
                    top: '50px',
                  }}
                >
                  <div className="text-xs font-semibold text-gray-500 mb-4">
                    Level {levelIdx}
                  </div>
                  {level.map((service, serviceIdx) => (
                    <div
                      key={service.id}
                      className="mb-6"
                      style={{ marginTop: `${serviceIdx * 120}px` }}
                    >
                      <div
                        className={`w-48 p-4 rounded-lg shadow-md cursor-pointer transition-all ${
                          selectedNode === service.name
                            ? 'ring-4 ring-blue-400 scale-105'
                            : 'hover:shadow-lg'
                        }`}
                        style={{
                          backgroundColor: getCloudProviderColor(service.cloud_provider),
                          color: 'white',
                        }}
                        onClick={() => setSelectedNode(service.name)}
                        data-testid={`node-${service.name}`}
                      >
                        <div className="font-semibold text-sm mb-1">{service.name}</div>
                        <div className="text-xs opacity-90">{service.service_type.replace('_', ' ')}</div>
                        <div className="flex items-center justify-between mt-2">
                          <Badge
                            variant="outline"
                            className="text-xs bg-white bg-opacity-20 border-white"
                          >
                            {service.cloud_provider}
                          </Badge>
                          {service.dependencies.length > 0 && (
                            <div className="text-xs opacity-90">
                              {service.dependencies.length} deps
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Node Details */}
      {selectedNode && (
        <Card>
          <CardHeader>
            <CardTitle>Service Details: {selectedNode}</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const service = services.find(s => s.name === selectedNode);
              if (!service) return null;

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Service Type</div>
                      <div className="font-medium">{service.service_type.replace('_', ' ')}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Cloud Provider</div>
                      <div className="font-medium">{service.cloud_provider}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Status</div>
                      <Badge className={service.is_active ? 'bg-green-100 text-green-800' : ''}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Health</div>
                      <Badge className="bg-green-100 text-green-800">{service.health_status}</Badge>
                    </div>
                  </div>

                  {service.dependencies.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-600 mb-2">Dependencies</div>
                      <div className="flex flex-wrap gap-2">
                        {service.dependencies.map((dep, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="cursor-pointer hover:bg-gray-100"
                            onClick={() => setSelectedNode(dep)}
                          >
                            {dep}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Find dependents */}
                  {(() => {
                    const dependents = services.filter(s =>
                      s.dependencies.includes(service.name)
                    );
                    if (dependents.length > 0) {
                      return (
                        <div>
                          <div className="text-sm text-gray-600 mb-2">Dependent Services</div>
                          <div className="flex flex-wrap gap-2">
                            {dependents.map((dep) => (
                              <Badge
                                key={dep.id}
                                variant="outline"
                                className="cursor-pointer hover:bg-gray-100"
                                onClick={() => setSelectedNode(dep.name)}
                              >
                                {dep.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServiceDependencyGraph;
